import React, { useEffect, useState } from "react";
import { api_Routes } from "../../tools/api_Routes";
import { Helper } from "../../tools/Helper";
import {
  LinearProgress,
  TablePagination,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Button,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import DataTable from "./data-table";
import { AddButton } from "../Button/AddButton";
import { Can } from "../Can";
import { useNavigate } from "react-router-dom";
import TemplateDialog from "./TemplateDialog";
import {setSuccess} from "../../Redux/AlertReducer";
import {useDispatch} from "react-redux";
import {errorToaster} from "../../helper/toasterConfiguration";

export const UsersTable = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [allData, setAllData] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGroupsLoading, setIsGroupsLoading] = useState(true);
  const [keyword, setKeyword] = useState("");
  const [debouncedKeyword, setDebouncedKeyword] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selected, setSelected] = useState(new Set());
  const [selectAllMode, setSelectAllMode] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [filters, setFilters] = useState({
    status: "all",
    verification: "all",
    groups: "all",
  });
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [templateType, setTemplateType] = useState('rich_text');
  // Add sorting state
  const [sortConfig, setSortConfig] = useState({
    field: 'id',
    direction: 'desc'
  });

  // Add balance dialog state
  const [balanceDialogOpen, setBalanceDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [balanceAmount, setBalanceAmount] = useState("");
  const [balanceType, setBalanceType] = useState("free"); // Default to 'free'
  const [balanceNote, setBalanceNote] = useState("");
  const dispatch = useDispatch();

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const handleKeywordChange = (event) => {
    setKeyword(event.target.value);
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setCurrentPage(0);
  };

  const handleGroupFilterChange = (event) => {
    const {
      target: { value },
    } = event;

    // If "all" is selected, clear all other selections
    if (value.includes("all")) {
      setFilters(prev => ({
        ...prev,
        groups: "all"
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        groups: value
      }));
    }
    setCurrentPage(0);
  };

  const handleRemoveGroupFilter = () => {
    setFilters(prev => ({
      ...prev,
      groups: "all"
    }));
    setCurrentPage(0);
  };

  // Add sort handler
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPage(0);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedKeyword(keyword);
    }, 300);
    return () => {
      clearTimeout(handler);
    };
  }, [keyword]);

  const getData = async () => {
    setIsLoading(true);
    const queryParams = [];

    if (debouncedKeyword) queryParams.push(`keyword=${debouncedKeyword}`);
    if (filters.status !== 'all') queryParams.push(`active=${filters.status === 'active'}`);
    if (filters.verification !== 'all') {
      queryParams.push(`verified=${filters.verification === 'verified'}`);
    }

    // Handle groups filter
    if (filters.groups !== 'all') {
      if (Array.isArray(filters.groups)) {
        queryParams.push(`groups=${JSON.stringify(filters.groups)}`);
      } else {
        queryParams.push(`groups=${filters.groups}`);
      }
    }

    // Add sorting parameters
    queryParams.push(`sort_by=${sortConfig.field}`);
    queryParams.push(`sort_order=${sortConfig.direction}`);

    const { response, message } = await Helper.Get({
      url: `${api_Routes.Users.View}?per_page=${rowsPerPage}&page=${
          currentPage + 1
      }${queryParams?.length > 0 ? `&${queryParams.join('&')}` : ""}`,
      hasToken: true,
    });

    if (response) {
      setData(response);
    }
    setIsLoading(false);
  };

  const getUserGroupsData = async () => {
    setIsGroupsLoading(true);

    const { response, message } = await Helper.Get({
      url: `${api_Routes.Users.Groups}`,
      hasToken: true,
    });

    if (response) {
      setUserGroups(response.data);
    }
    setIsGroupsLoading(false);
  };

  const handleSelectionChange = (newSelected, newSelectAllMode) => {
    setSelected(newSelected);
    setSelectAllMode(newSelectAllMode);
  };

  const handleTemplateSelect = (template) => {
    setTemplateDialogOpen(false);

    const route = template.type === 'rich_text'
        ? '/email-builder'
        : '/email-template';

    navigate(route, {
      state: {
        selectedIds: Array.from(selected),
        selectAllMode,
        filters,
        template
      }
    });
  };

  const handleCreateNewTemplate = () => {
    setTemplateDialogOpen(false);

    const route = templateType === 'rich_text'
        ? '/email-builder'
        : '/email-template';

    navigate(route, {
      state: {
        selectedIds: Array.from(selected),
        selectAllMode,
        filters
      }
    });
  };

  const handleBulkAction = async (action) => {
    if (action === 'send_email') {
      setTemplateType('rich_text');
      setSelectedAction(action);
      setTemplateDialogOpen(true);
      return;
    }
    if (action === 'send_email_template') {
      setTemplateType('structured');
      setSelectedAction(action);
      setTemplateDialogOpen(true);
      return;
    }
    if (action === 'send_ticket') {
      navigate('/ticket-builder', {
        state: {
          selectedIds: Array.from(selected),
          selectAllMode,
          filters
        }
      });
      return;
    }
    setIsLoading(true);
    try {
      const ids = selectAllMode
          ? 'ALL'
          : Array.from(selected);

      const payload = {
        action,
        ids,
        filters: selectAllMode ? filters : null
      };

      const { response } = await Helper.Post({
        url: api_Routes.Users.BulkActions,
        data: payload,
        hasToken: true
      });

      await getData();
      await getUserGroupsData();
      setSelected(new Set());
      setSelectAllMode(false);
    } catch (error) {
      console.error("Bulk action failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Add balance dialog functions
  const handleOpenBalanceDialog = (user) => {
    setSelectedUser(user);
    setBalanceAmount("");
    setBalanceType("free"); // Reset to default
    setBalanceNote("");
    setBalanceDialogOpen(true);
  };

  const handleCloseBalanceDialog = () => {
    setBalanceDialogOpen(false);
    setSelectedUser(null);
    setBalanceAmount("");
    setBalanceType("free");
    setBalanceNote("");
  };

  const handleBalanceSubmit = async () => {
    if (!balanceAmount || isNaN(balanceAmount)) {
      alert("Please enter a valid balance amount");
      return;
    }

    setIsLoading(true);
    try {
      // Call your API to update user balance
      const { response, message } = await Helper.Put({
        url: `${api_Routes.Users.AddBalance}${selectedUser.id}`,
        data: {
          balance: parseFloat(balanceAmount),
          balance_type: balanceType, // Add balance type
          note: balanceNote, // Add note
          // Include other necessary fields if required by your API
          name: selectedUser.name,
          email: selectedUser.email
        },
        hasToken: true,
      });

      if (response) {
        // Refresh the data
        await getData();
        handleCloseBalanceDialog();
      } else {
        errorToaster(message);
        handleCloseBalanceDialog();
      }
    } catch (error) {
      console.error("Balance update failed:", error);
      alert("An error occurred while updating balance");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getData();
    getUserGroupsData();
  }, [debouncedKeyword, currentPage, rowsPerPage, filters, sortConfig]);

  return (
      <div>
        <div className="mb-4">
          <div className="flex flex-wrap gap-4 items-center mb-3">
            <label className="font-bold inline-block text-textColor dark:text-darkTextColor text-[15px] text-start">
              Search :
              <input
                  placeholder="Search by name, email, or panel URL"
                  type="text"
                  name="keyword"
                  value={keyword}
                  onChange={handleKeywordChange}
                  autoComplete="off"
                  className="duration-200 font-normal bg-secondary dark:bg-darkPrimary text-textColor dark:text-darkTextColor ml-2
                    border-2 input-box rounded-[0.25rem] py-1 px-3 outline-none"
              />
            </label>

            <FormControl size="small" className="min-w-[120px]">
              <InputLabel>Status</InputLabel>
              <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" className="min-w-[120px]">
              <InputLabel>Verification</InputLabel>
              <Select
                  value={filters.verification}
                  label="Verification"
                  onChange={(e) => handleFilterChange('verification', e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="verified">Verified</MenuItem>
                <MenuItem value="unverified">Unverified</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" className="min-w-[180px]">
              <InputLabel>Groups</InputLabel>
              <Select
                  multiple
                  value={filters.groups === "all" ? [] : filters.groups}
                  onChange={handleGroupFilterChange}
                  input={<OutlinedInput label="Groups" />}
                  renderValue={(selected) => {
                    if (selected.length === 0 || filters.groups === "all") {
                      return "All Groups";
                    }
                    return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => {
                            const group = userGroups.find(g => g.id === value);
                            return (
                                <Chip key={value} label={group ? group.name : value} size="small" />
                            );
                          })}
                        </Box>
                    );
                  }}
              >
                <MenuItem value="all">
                  <Checkbox checked={filters.groups === "all"} />
                  <ListItemText primary="All Groups" />
                </MenuItem>
                {userGroups?.map((varItem) => (
                    <MenuItem key={varItem.id} value={varItem.id}>
                      <Checkbox checked={filters.groups !== "all" && filters.groups.includes(varItem.id)} />
                      <ListItemText primary={varItem.name} />
                    </MenuItem>
                ))}
              </Select>
            </FormControl>

            <div className="flex gap-2">
              {filters.status !== 'all' && (
                  <Chip
                      label={`Status: ${filters.status}`}
                      onDelete={() => handleFilterChange('status', 'all')}
                  />
              )}
              {filters.verification !== 'all' && (
                  <Chip
                      label={`Verification: ${filters.verification}`}
                      onDelete={() => handleFilterChange('verification', 'all')}
                  />
              )}
              {filters.groups !== 'all' && (
                  <Chip
                      label={`Groups: ${Array.isArray(filters.groups) ? filters.groups.map(id => {
                        const group = userGroups.find(g => g.id === id);
                        return group ? group.name : id;
                      }).join(', ') : filters.groups}`}
                      onDelete={handleRemoveGroupFilter}
                  />
              )}
            </div>

            <div className="ml-auto">
              <Can I="store" a="users">
                <AddButton to="/Users/Add" label="Add User" />
              </Can>
            </div>
          </div>
        </div>

        {(selected.size > 0 || selectAllMode) && (
            <div className="bg-gray-100 p-3 mb-3 rounded flex items-center">
          <span className="mr-4">
            {selectAllMode
                ? `All ${data?.pagination?.total || 0} selected (filtered)`
                : `${selected.size} selected`}
          </span>

              <div className="relative">
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    endIcon={<span className="ml-1">▼</span>}
                    className="mr-2"
                >
                  Bulk Actions
                </Button>

                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                >
                  <MenuItem onClick={() => {
                    handleBulkAction('activate');
                    setAnchorEl(null);
                  }}>
                    Activate Selected
                  </MenuItem>
                  <MenuItem onClick={() => {
                    handleBulkAction('deactivate');
                    setAnchorEl(null);
                  }}>
                    Deactivate Selected
                  </MenuItem>
                  <MenuItem onClick={() => {
                    handleBulkAction('verify_email');
                    setAnchorEl(null);
                  }}>
                    Verify Email
                  </MenuItem>
                  <MenuItem onClick={() => {
                    handleBulkAction('unverify_email');
                    setAnchorEl(null);
                  }}>
                    Unverify Email
                  </MenuItem>
                  <MenuItem onClick={() => {
                    handleBulkAction('send_email');
                    setAnchorEl(null);
                  }}>
                    Send Email
                  </MenuItem>
                  <MenuItem onClick={() => {
                    handleBulkAction('send_email_template');
                    setAnchorEl(null);
                  }}>
                    Send Email with template
                  </MenuItem>
                  <MenuItem onClick={() => {
                    handleBulkAction('send_ticket');
                    setAnchorEl(null);
                  }}>
                    Send Ticket
                  </MenuItem>
                </Menu>
              </div>

              <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setSelected(new Set());
                    setSelectAllMode(false);
                  }}
              >
                Clear
              </Button>
            </div>
        )}

        {isLoading ? (
            <LinearProgress sx={{ marginTop: 1 }} />
        ) : data && data?.data?.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <DataTable
                    data={data?.data}
                    allData={allData}
                    selected={selected}
                    selectAllMode={selectAllMode}
                    onSelectionChange={handleSelectionChange}
                    totalCount={data?.pagination?.total}
                    page={currentPage}
                    rowsPerPage={rowsPerPage}
                    onAddBalance={handleOpenBalanceDialog}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
              </div>
              <div className="mt-4 flex justify-center items-center">
                <TablePagination
                    page={currentPage}
                    className="mt-auto"
                    onPageChange={handlePageChange}
                    count={data.pagination.total}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    variant="outlined"
                    shape="rounded"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                />
              </div>
            </>
        ) : (
            <TableContainer>
              <Table aria-label="collapsible table">
                <TableHead>
                  <TableRow>
                    <TableCell
                        align="center"
                        sx={{
                          backgroundColor: "primary.main",
                          color: "white",
                          fontWeight: "bold",
                        }}
                    >
                      There are no data yet
                    </TableCell>
                  </TableRow>
                </TableHead>
              </Table>
            </TableContainer>
        )}

        <TemplateDialog
            open={templateDialogOpen}
            type={templateType}
            onClose={() => setTemplateDialogOpen(false)}
            onSelect={handleTemplateSelect}
            onCreateNew={handleCreateNewTemplate}
        />

        {/* Updated Add Balance Dialog */}
        <Dialog open={balanceDialogOpen} onClose={handleCloseBalanceDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Add Balance to {selectedUser?.name}</DialogTitle>
          <DialogContent>
            <TextField
                autoFocus
                margin="dense"
                label="Balance Amount"
                type="number"
                fullWidth
                variant="outlined"
                value={balanceAmount}
                onChange={(e) => setBalanceAmount(e.target.value)}
                inputProps={{  step: "0.01" }}
                sx={{ mb: 2 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Balance Type</InputLabel>
              <Select
                  value={balanceType}
                  label="Balance Type"
                  onChange={(e) => setBalanceType(e.target.value)}
              >
                <MenuItem value="free">Free</MenuItem>
                <MenuItem value="bonus">Bonus</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>

            <TextField
                margin="dense"
                label="Note (Optional)"
                type="text"
                fullWidth
                variant="outlined"
                value={balanceNote}
                onChange={(e) => setBalanceNote(e.target.value)}
                multiline
                rows={3}
                placeholder="Add any notes about this balance addition..."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseBalanceDialog}>Cancel</Button>
            <Button onClick={handleBalanceSubmit} variant="contained">
              Add Balance
            </Button>
          </DialogActions>
        </Dialog>
      </div>
  );
};